import {
	Button,
	Checkbox,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	EmptyState,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link } from "react-router";
import { useAccess } from "~/core/access/index";
import { ApiError, api, type InsightAudience } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

const PROFILE_FIELDS = [
	"job_title",
	"seniority",
	"industry",
	"company_size",
	"age_range",
	"motivations",
	"pain_points",
] as const;
export function AudienceSettings({ entityId }: { entityId: string }) {
	return (
		<div id="audiences">
			<AudienceList entityId={entityId} kind="region" />
			<AudienceList entityId={entityId} kind="persona" />
		</div>
	);
}
function AudienceList({ entityId, kind }: { entityId: string; kind: "region" | "persona" }) {
	const t = useT();
	const { editor, why } = useAccess();
	const [editing, setEditing] = useState<InsightAudience | "new" | null>(null);
	const plural = kind === "region" ? "regions" : "personas";
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "audiences", plural],
		queryFn: () => api.insights.audiences(entityId, plural, { range: "7d", locale: "en" }),
		...read,
	});
	return (
		<Section
			title={t(`reading.${plural}`)}
			lede={t(`audience.${plural}.help`)}
			action={
				<Button
					variant="default"
					disabled={!editor}
					title={!editor ? why.editor : undefined}
					onClick={() => setEditing("new")}
				>
					{t(`audience.add.${kind}`)}
				</Button>
			}
		>
			<Async state={state}>
				{(data) => (
					<>
						{kind === "region" && <p className="form-note">{t("audience.regionPending")}</p>}
						{data.definitions.length === 0 ? (
							<EmptyState size="compact" title={t(`audience.${plural}.empty`)} />
						) : (
							<div className="table-scroll">
								<Table variant="resource">
									<TableHeader>
										<TableRow>
											<TableHead>{t(`${plural}.axis`)}</TableHead>
											<TableHead>{t("audience.scope")}</TableHead>
											<TableHead>{t("audience.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.definitions.map((a) => (
											<TableRow key={a.id}>
												<TableCell>
													{a.name}
													{a.archived && <span className="faint"> · {t("audience.archived")}</span>}
												</TableCell>
												<TableCell>
													{kind === "region"
														? a.profile.country
														: a.topics.length + a.question_ids.length === 0
															? t("audience.allPrompts")
															: `${a.topics.length} ${t("audience.topics")} · ${a.question_ids.length} ${t("audience.prompts")}`}
												</TableCell>
												<TableCell>
													<Button size="sm" variant="ghost" disabled={!editor} onClick={() => setEditing(a)}>
														{t("audience.edit")}
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
						<Link to={`/w/${entityId}/insights/${plural}`}>{t("audience.view")}</Link>
					</>
				)}
			</Async>
			{editing && (
				<AudienceEditor
					countries={state.data?.countries ?? []}
					key={editing === "new" ? "new" : editing.id}
					entityId={entityId}
					kind={kind}
					initial={editing === "new" ? undefined : editing}
					onClose={() => setEditing(null)}
				/>
			)}
		</Section>
	);
}
function AudienceEditor({
	countries,
	entityId,
	kind,
	initial,
	onClose,
}: {
	entityId: string;
	kind: "region" | "persona";
	countries: string[];
	initial?: InsightAudience;
	onClose: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const cache = useQueryClient();
	const formId = useId();
	const [name, setName] = useState(initial?.name ?? "");
	const [profile, setProfile] = useState<Record<string, string>>(initial?.profile ?? {});
	const [topics, setTopics] = useState(initial?.topics ?? []);
	const [questionIds, setQuestionIds] = useState(initial?.question_ids ?? []);
	const [archived, setArchived] = useState(initial?.archived ?? false);
	const [language, setLanguage] = useState("en");
	const prompts = useQuery({
		queryKey: [...keys.entity(entityId), "audience-prompts", language],
		queryFn: () => api.insights.prompts(entityId, { locale: language, range: "30d" }),
		enabled: kind === "persona",
		...read,
	});
	const save = useMutation({
		mutationFn: () =>
			api.saveAudience(entityId, {
				...(initial ? { id: initial.id } : {}),
				kind,
				name,
				profile,
				topics,
				question_ids: questionIds,
				archived,
			}),
		onSuccess: async () => {
			await cache.invalidateQueries({ queryKey: keys.entity(entityId) });
			onClose();
		},
	});
	const title = t(initial ? "audience.edit" : `audience.add.${kind}`);
	const renderField = (field: (typeof PROFILE_FIELDS)[number]) => (
		<div className="grid gap-(--s-2)" key={field}>
			<Label htmlFor={`${formId}-${field}`}>{t(`audience.field.${field}`)}</Label>
			{field === "motivations" || field === "pain_points" ? (
				<Textarea
					id={`${formId}-${field}`}
					rows={2}
					maxLength={1000}
					value={profile[field] ?? ""}
					onChange={(e) => setProfile({ ...profile, [field]: e.target.value })}
				/>
			) : (
				<Input
					id={`${formId}-${field}`}
					maxLength={1000}
					value={profile[field] ?? ""}
					onChange={(e) => setProfile({ ...profile, [field]: e.target.value })}
				/>
			)}
		</div>
	);

	return (
		<ChipModal
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
			dismissDisabled={save.isPending}
			srTitle={title}
		>
			<ChipModalHeader onClose={onClose} closeAriaLabel={t("common.close")}>
				{title}
			</ChipModalHeader>
			<ChipModalBody className="px-(--s-8)">
				<form
					id={formId}
					onSubmit={(e) => {
						e.preventDefault();
						if (editor && !save.isPending && name.trim() && (kind !== "region" || profile.country)) save.mutate();
					}}
				>
					<fieldset className="bare-fieldset stack" disabled={!editor || save.isPending}>
						<Label htmlFor={`${formId}-name`}>{t("audience.name")}</Label>
						<Input
							id={`${formId}-name`}
							value={name}
							maxLength={100}
							required
							onChange={(e) => setName(e.target.value)}
						/>
						{kind === "region" ? (
							<>
								<Label htmlFor={`${formId}-country`}>{t("audience.country")}</Label>
								<ChipSelect
									id={`${formId}-country`}
									value={profile.country ?? ""}
									searchable
									searchPlaceholder={t("table.search")}
									emptyLabel={t("audience.noResults")}
									stayBelow
									fullWidth
									aria-label={t("audience.country")}
									placeholder={t("audience.selectCountry")}
									options={countries.map((code) => ({
										value: code,
										label: new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code,
									}))}
									onChange={(country) => {
										setProfile({ country });
										if (
											!name.trim() ||
											name === new Intl.DisplayNames([locale], { type: "region" }).of(profile.country || country)
										)
											setName(new Intl.DisplayNames([locale], { type: "region" }).of(country) ?? country);
									}}
								/>
								<p className="form-note">{t("audience.regionPending")}</p>
							</>
						) : (
							<>
								{(["job_title", "motivations", "pain_points"] as const).map(renderField)}
								<details>
									<summary>{t("audience.moreAttributes")}</summary>
									<div className="grid gap-(--s-4) sm:grid-cols-2">
										{PROFILE_FIELDS.filter((f) => !["job_title", "motivations", "pain_points"].includes(f)).map(
											renderField,
										)}
									</div>
								</details>
								<details open={topics.length + questionIds.length > 0 || undefined}>
									<summary>
										{t("audience.scope")} ·{" "}
										{topics.length + questionIds.length === 0
											? t("audience.allPrompts")
											: topics.length + questionIds.length}
									</summary>
									<div className="stack">
										<Label>{t("audience.promptLanguage")}</Label>
										<ChipSelect
											value={language}
											onChange={setLanguage}
											aria-label={t("audience.promptLanguage")}
											options={[
												{ value: "en", label: "English" },
												{ value: "zh", label: "中文" },
											]}
										/>
										<Async state={prompts}>
											{(data) => (
												<>
													<Label>{t("audience.topics")}</Label>
													<ChipSelect
														searchable
														searchPlaceholder={t("table.search")}
														emptyLabel={t("audience.noResults")}
														stayBelow
														multiSelect
														multiSelectValues={topics}
														onMultiSelectChange={setTopics}
														aria-label={t("audience.topics")}
														placeholder={t("audience.selectTopics")}
														options={[...new Set([...topics, ...data.topics])].map((v) => ({ value: v, label: v }))}
													/>
													<Label>{t("audience.prompts")}</Label>
													<ChipSelect
														searchable
														searchPlaceholder={t("table.search")}
														emptyLabel={t("audience.noResults")}
														stayBelow
														multiSelect
														multiSelectValues={questionIds}
														onMultiSelectChange={setQuestionIds}
														aria-label={t("audience.prompts")}
														placeholder={t("audience.selectPrompts")}
														options={[
															...new Map(
																[
																	...questionIds.map((id) => ({ value: id, label: id })),
																	...data.prompts.map((q) => ({ value: q.id, label: q.text })),
																].map((o) => [o.value, o]),
															).values(),
														]}
													/>
												</>
											)}
										</Async>
										<p className="hint">{t("audience.assignmentHelp")}</p>
									</div>
								</details>
							</>
						)}
						{initial && (
							<Label>
								<Checkbox checked={archived} onCheckedChange={(v) => setArchived(v === true)} /> {t("audience.archive")}
							</Label>
						)}
						{save.error && (
							<p role="alert">{save.error instanceof ApiError ? save.error.localized(locale) : t("audience.error")}</p>
						)}
					</fieldset>
				</form>
			</ChipModalBody>
			<ChipModalFooter
				onCancel={onClose}
				cancelLabel={t("common.cancel")}
				primaryAction={{
					label: t(save.isPending ? "audience.saving" : "common.save"),
					form: formId,
					type: "submit",
					disabled: !editor || save.isPending || !name.trim() || (kind === "region" && !profile.country),
				}}
			/>
		</ChipModal>
	);
}
